import { useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { queryKeys } from "@/api/query-keys";
import { Surface, Text } from "@/shared/ui";
import { CheckIcon } from "./icons";

/**
 * Handles the post-checkout `?checkout=success` return. Polar redirects the user
 * back into the app (to `/chat`) after payment, so this lives at the app root —
 * route-agnostic. It refetches `me` + billing (so the sidebar plan badge and
 * usage flip to the new plan), strips the param, and shows a confirmation toast.
 * The plan itself is flipped server-side by the Polar webhook.
 */
export function CheckoutReturnHandler() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const isSuccess = new URLSearchParams(searchStr).get("checkout") === "success";
    if (!isSuccess) return;

    void queryClient.invalidateQueries({ queryKey: queryKeys.me.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    setShowToast(true);

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      // History API unavailable (e.g. tests) — the refetch above still ran.
    }
  }, [searchStr, queryClient]);

  useEffect(() => {
    if (!showToast) return;
    const id = window.setTimeout(() => setShowToast(false), 6000);
    return () => window.clearTimeout(id);
  }, [showToast]);

  if (!showToast) return null;

  return createPortal(
    <div className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      <Surface
        role="status"
        tone="paper"
        radius="md"
        border="hairline"
        shadow="pill"
        className="flex items-center gap-3 px-4 py-3"
      >
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-ink text-paper">
          <CheckIcon className="size-3.5" />
        </span>
        <Text variant="ui" as="span">
          {t("pricing.checkoutSuccess")}
        </Text>
      </Surface>
    </div>,
    document.body,
  );
}
